import { MobileViewPath } from '.'

interface QueryParams {
  [key: string]: any
}

const omitEmptyValues = <T>(obj: T): Partial<T> => {
  for (const propName in obj) {
    if (!obj[propName]) {
      delete obj[propName]
    }
  }
  return obj
}

export const appendParams = (url: string, params?: QueryParams): string => {
  const params_ = omitEmptyValues(params || {}) as Record<string, string>
  const search = new URLSearchParams(params_).toString()
  const divider = url.includes('?') ? '&' : '?'
  const fullUrl = `${url}${divider}${search}`

  return fullUrl.endsWith('?') ? fullUrl.slice(0, -1) : fullUrl
}

/*
 * Dashboard page
 */

export const getDashboardPageUrl = (slug: string): string => appendParams(`/${slug}/dashboard`, {})

/*
 * Bank integration flow page
 */
export const getBankIntegrationPageURL = (
  slug: string,
  page: 'aspsps' | 'holvi-edit' | 'holvi-finalize' | 'finalize' | 'edit' | 'failed',
  id?: number
): string => appendParams(`/${slug}/bank-integration-flow/${page}`, id ? { id } : {})

/*
 * Documents page
 */

export interface IDocumentsPageURLParams {
  id?: number | null
  tab?: 'overview' | 'import_data' | 'suggestions' | 'audit_trail'
  extra?: 'import_data'
  create?: '1'
  draft?: '1'
}

export const getDocumentsPageUrl = (slug: string, params?: IDocumentsPageURLParams): string =>
  appendParams(`/${slug}/documents`, params)

/*
 * Accounts page
 */

export const getAccountsPageUrl = (slug: string): string => appendParams(`/${slug}/accounts`, {})

interface IAccountsPageURLParams {
  id: number
}

export const getAccountPageUrl = (slug: string, params?: IAccountsPageURLParams): string =>
  appendParams(`/${slug}/accounts`, params)

/*
 * Files page
 */

interface IFilesPageURLParams {
  id: number | null
  idType: 'file' | 'folder' | null
  folder?: number
}

export const getFilesPageUrl = (slug: string, params?: IFilesPageURLParams): string =>
  appendParams(`/${slug}/files`, params)

/*
 * Settings page
 */

interface ISettingsPageURLParams {
  page?:
    | 'settings'
    | 'automation'
    | 'automation-vat-settings'
    | 'automation-opening-balance'
    | 'automation-rules'
    | 'automation-default-accounts'
    | 'people'
    | 'general-danger-zone'
    | 'integrations'
    | 'plan'
    | 'plan-management'
    | 'plan-packages'
    | 'luca-usage'
    | 'luca-connections'
    | 'luca'
    | 'invoicing'
    | 'invoicing-general'
    | 'invoicing-debt-collection'
    | 'invoicing-advanced'
    | 'holvi'
    | 'payments'
    | 'einvoicing'
    | 'data-export'
    | 'tags'
    | 'change-log'
    | 'verohallinto'
    | 'elster'
    | 'datev'
    | 'referral'
    | null
  tab?: string
  panel?: string
  modal?: 'business-info' | 'business-identifiers'
  /** Accounting rule id — opens the rule editor on the automation-rules page. */
  id?: number
}

export const getSettingsPageUrl = (slug: string, params?: ISettingsPageURLParams): string =>
  appendParams(`/${slug}/settings`, params)

/** Tabs the Luca widget can ask the parent to open via `AI_WIDGET_OPEN_LUCA_SETTINGS`. */
export type LucaWidgetSettingsTab =
  | 'business'
  | 'connectors'
  | 'usage'
  | 'plan'
  | 'bundles'
  | 'payg'

export const getLucaWidgetSettingsPageParams = (
  tab: LucaWidgetSettingsTab
): ISettingsPageURLParams => {
  switch (tab) {
    case 'business':
      return { page: 'luca' }
    case 'connectors':
      return { page: 'luca-connections' }
    case 'usage':
      return { page: 'luca-usage' }
    case 'bundles':
      return { page: 'luca-usage', panel: 'bundles' }
    case 'payg':
      return { page: 'luca-usage', panel: 'payg' }
    case 'plan':
      return { page: 'plan-management', panel: 'subscription' }
    default: {
      const _exhaustive: never = tab
      return _exhaustive
    }
  }
}

/*
 * Period page
 */

interface IPeriodPageURLParams {
  id: number
  tab?: 'details' | 'financial-statement'
}

export const getPeriodPageUrl = (slug: string, params?: IPeriodPageURLParams): string =>
  appendParams(`/${slug}/accounting`, params)

/*
 * VAT page
 */

interface IVatPageURLParams {
  id: number
}

export const getVATPageUrl = (slug: string, params?: IVatPageURLParams): string =>
  appendParams(`/${slug}/vat`, params)

/*
 * Product page
 */

interface IProductPageURLParams {
  id: number
}

export const getProductPageUrl = (slug: string, params?: IProductPageURLParams): string =>
  appendParams(`/${slug}/products`, params)

/*
 * Invoicing page
 */

interface IInvoicingPageSalesOpenParams {
  id: number
  type: 'sales'
}
interface IInvoicingPagePurchaseOpenParams {
  id: number
  type: 'purchase'
}

interface IInvoicingPageCreateParams {
  create: '1'
}

type IInvoicingPageURLParams =
  | IInvoicingPageSalesOpenParams
  | IInvoicingPagePurchaseOpenParams
  | IInvoicingPageCreateParams

export const getInvoicingPageUrl = (slug: string, params?: IInvoicingPageURLParams): string =>
  appendParams(`/${slug}/invoicing`, params)

/*
 * Contact page
 */

interface IContactPageURLParams {
  id?: number
  create?: '1'
}

export const getContactPageUrl = (slug: string, params?: IContactPageURLParams): string =>
  appendParams(`/${slug}/contacts`, params)

export type WorkflowsHubTab = 'workflows' | 'rules' | 'accrual'

interface IWorkflowsPageURLParams {
  tab?: WorkflowsHubTab
  /** Accounting rule id — opens the rule editor on the rules tab. */
  id?: number
}

export const getWorkflowsPageUrl = (slug: string, params?: IWorkflowsPageURLParams): string =>
  appendParams(`/${slug}/workflows`, params)

interface IWorkflowBuilderPageURLParams {
  tab?: 'runs' | 'edit'
  run?: number
  node?: string
}

export const getWorkflowBuilderPageUrl = (
  slug: string,
  workflowId: number | string,
  params?: IWorkflowBuilderPageURLParams
): string => appendParams(`/${slug}/workflows/${workflowId}`, params)

export const getIfInsurancePageUrl = (slug: string) => {
  return `/${slug}/if-insurance`
}

export const getUserSettingsUrl = () => {
  return `${import.meta.env.VITE_AUTH_BASE_URL}/auth/email/`
}

export const getMobileViewUrls = (slug: string, path: MobileViewPath) => {
  return `/mobile/${slug}/${path}`
}
